import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Autocomplete, Stack, TextField, useTheme } from '@mui/material';
import { type AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { type FC, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';

import { useAllCodes } from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';
import { type Code } from '../../models/index.js';
import { useMutation } from '../../utils/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';

const logFormSchema = Yup.object({
  code: Yup.mixed<Code>().required('Code is a required field.'),
  metadata: Yup.mixed<{ [id: string]: string }>().optional().default({}),
});

interface LogFormValues extends Yup.InferType<typeof logFormSchema> {
  code: Code;
  metadata: { [id: string]: string };
}
const defaultValues: LogFormValues = {
  code: {} as Code,
  metadata: {},
};
interface LogFormProps {
  patientId?: string;
  onSuccess?: (response: AxiosResponse) => void;
}
const LogForm: FC<LogFormProps> = ({ patientId, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues,
    resolver: yupResolver(logFormSchema),
  });
  const code = useWatch({ control, name: 'code' });
  const theme = useTheme();
  const { data: codes = [] } = useAllCodes({
    includeMetadata: true,
  });
  const snackBar = useSnackBar();

  const createMutation = useMutation({
    path: '/logs',
    method: 'POST',
    onSuccess,
  });

  const onSubmit = (values: LogFormValues, callback: () => void) => {
    const { code, metadata } = values;

    if (!patientId) {
      snackBar.error('Log not saved. No patient ID.');
      return;
    }
    createMutation.mutate(
      {
        patientId,
        codeId: code.id,
        metadata,
      },
      { onSuccess: callback },
    );
  };

  return (
    <form
      onSubmit={handleSubmit((data) => {
        setIsSubmitting(true);
        onSubmit(data, () => setTimeout(() => setIsSubmitting(false), theme.transitions.duration.leavingScreen));
      })}
    >
      <Stack spacing={2}>
        <Controller
          name={'code'}
          control={control}
          render={({ field }) => {
            return (
              <Autocomplete
                autoFocus
                value={!_.isEmpty(field.value) ? field.value : null}
                onChange={(_: any, value: Code | null) => field.onChange(value)}
                id={'code-log-select'}
                options={codes}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(code: Code) => (!_.isEmpty(code) ? `${code.code} - ${code.description}` : '')}
                renderInput={(params) => <TextField {...params} InputLabelProps={{ required: true }} label={'Code'} />}
                renderOption={({ key, ...props }, option, { inputValue }) => (
                  <AutocompleteOptions option={`${option.code} - ${option.description}`} value={inputValue} key={key} {...props} />
                )}
              />
            );
          }}
        />
        {code?.metadata?.map((metadata, i) => (
          <Controller
            key={`metadata[${code!.metadata![i].id}]`}
            // @ts-expect-error react-hook-form Controller `name` prop typing doesn't accept dynamic dotted paths
            name={`metadata[${code!.metadata![i].id}]`}
            control={control}
            render={({ field }) => (
              <TextField {...field} multiline={metadata.inputType === 'textarea'} label={metadata.prompt} />
            )}
          />
        ))}

        <LoadingButton type={'submit'} loading={isSubmitting} variant={'contained'} disabled={!isDirty}>
          Save
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default LogForm;
